import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { BadgeList } from '@wix/members-badge-lib';

import withTranslate from '../../hoc/with-translate';
import RoleIcons from '../../containers/role-icons';
import styles from './member-box-mobile.scss';
import Blocked from '../blocked';
import Show from '../../hoc/show';
import Avatar from '../avatar';
import MemberMoreButton from '../member-more-button';
import { preventDefault } from '../../services/click-handling';
import { FollowButton } from '../follow-button';
import { ChatButton } from '../chat-button';
import { Button } from '../button';
import { LAYOUT_FF_PAGE_VIEW } from '../../constants/layout-types';

class MemberBox extends React.Component {
  renderActionButtons() {
    const { member, t, goToMemberPage, followOrUnfollow, showChat, handleChat, currentMemberUid } = this.props;
    const showMyProfileButton = member.uid === currentMemberUid && (this.props.showFollowButton || showChat);
    const showChatButton = showChat && member.isSubscribed && !showMyProfileButton;
    const showFollowButton = !showChatButton && !showMyProfileButton && this.props.showFollowButton;

    return (
      <div className={styles.button}>
        {showMyProfileButton && (
          <Button
            dataHook="my-profile-link"
            onClick={preventDefault(() => goToMemberPage(member.uid))}
            className={classNames(
              styles.buttonAction,
              'ma-pw-button-font',
              'ma-secondary-button',
              'ma-members-button-font-secondary',
              'ma-all-members-buttons-corner-radius',
            )}
          >
            {t('members-page.my-profile')}
          </Button>
        )}
        {showFollowButton && (
          <FollowButton
            onClick={preventDefault(() => followOrUnfollow(member.uid))}
            className={classNames(styles.buttonAction, 'ma-pw-button-font')}
            dataHook="members-list-follow"
            isFollowing={member.isSubscribed}
            followText={t('profile-widget.follow')}
            unfollowText={t('profile-widget.unfollow')}
            primaryButtonClass={classNames('ma-pw-primary-button', 'ma-primary-button')}
            secondaryButtonClass={classNames('ma-secondary-button', 'ma-fill-primary-text')}
            type="text"
          />
        )}
        {showChatButton && (
          <ChatButton
            onClick={preventDefault(() => handleChat(member.uid))}
            className={classNames(styles.buttonAction, 'ma-pw-button-font')}
            secondaryButtonClass={classNames('ma-pw-primary-button', 'ma-primary-button')}
            primaryButtonClass={classNames('ma-secondary-button', 'ma-fill-primary-text')}
            isFollowing={member.isSubscribed}
            size="small"
            text={t('profile-widget.message')}
            type="text"
          />
        )}
      </div>
    );
  }

  render() {
    const {
      member,
      t,
      pictureStyle,
      rolesActions,
      showFollowers,
      onRoleAction,
      goToMemberPage,
      className,
      layoutStyle,
      showAsBlocked,
      badgesSettings,
      showBadges,
    } = this.props;

    const containerClassName = classNames(
      'ma-members-member-box',
      'ma-members-member-box-border-color',
      styles.container,
      className,
      { [styles.ffPageView]: layoutStyle === LAYOUT_FF_PAGE_VIEW },
    );

    return (
      <div data-hook="memberBox" className={containerClassName}>
        <div className={styles.boxLink} onClick={() => goToMemberPage(member.uid)} />

        <div className={styles.padding}>
          <div className={styles.pictureCont} onClick={() => goToMemberPage(member.uid)}>
            <Avatar
              url={member.picture}
              layoutType={pictureStyle}
              className={styles.avatar}
              name={member.name}
              isMobile={true}
            />
          </div>
          <div className={styles.center} onClick={() => goToMemberPage(member.uid)}>
            <div className={styles.nameAndIcon}>
              <div className={classNames(styles.nameText, 'ma-members-name-font')}>
                <span>{member.name}</span>
              </div>
              <RoleIcons
                roles={member.roles}
                containerClassName={styles.roleIcons}
                iconClassName="ma-fill-primary-text"
              />
            </div>
            <div className={styles.clear} />
            <Show when={showFollowers && !showAsBlocked}>
              <div className={classNames(styles.follow, 'ma-members-ff-font')}>
                <div className={styles.followSide} data-hook="mb-followers">
                  <span>{member.followerCount}</span>
                  <span>{t('profile-widget.followers')}</span>
                </div>
                <div className={styles.followSide} data-hook="mb-following">
                  <span>{member.followingCount}</span>
                  <span>{t('profile-widget.following')}</span>
                </div>
              </div>
            </Show>
            <div className={styles.clear} />
            {showBadges && (
              <div className={styles.badgesList}>
                <BadgeList
                  {...badgesSettings}
                  maxRows={1}
                  align="left"
                  size="tiny"
                  onClickMore={() => goToMemberPage(member.uid)}
                  onBadgeClick={() => goToMemberPage(member.uid)}
                />
              </div>
            )}
            {showAsBlocked && (
              <Blocked member={member} className={classNames('ma-primary-font', styles.blockedMember)} />
            )}
          </div>

          {this.renderActionButtons()}

          <div className={styles.moreButtonWrapper}>
            <MemberMoreButton
              rolesActions={rolesActions}
              member={member}
              onRoleAction={onRoleAction}
              containerClass={styles.actions}
              iconContainerClass={styles.iconContainer}
            />
          </div>
          <div className={styles.clear} />
        </div>
      </div>
    );
  }
}

MemberBox.propTypes = {
  member: PropTypes.object.isRequired,
  followOrUnfollow: PropTypes.func,
  pictureStyle: PropTypes.number,
  currentMemberUid: PropTypes.string,
  t: PropTypes.func,
  isSocial: PropTypes.bool,
  onRoleAction: PropTypes.func,
  rolesActions: PropTypes.array,
  showFollowers: PropTypes.bool,
  goToMemberPage: PropTypes.func,
  goToFFPage: PropTypes.func,
  className: PropTypes.string,
  layoutStyle: PropTypes.number,
  showAsBlocked: PropTypes.bool,
  showChat: PropTypes.bool,
  handleChat: PropTypes.func,
  showFollowButton: PropTypes.bool,
  showBadges: PropTypes.bool,
};

export default withTranslate(MemberBox);
